import { useMemo, useState } from "react";
import { X, CaretLeft, CaretRight, Calendar } from "@phosphor-icons/react";

/**
 * Calendar-style date range picker modal.
 * Granularity toggle: Day / Week / Month / Year.
 *
 * Emits `{from, to}` (ISO YYYY-MM-DD) via onApply.
 */
const MONTH_LABELS = [
  "Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio",
  "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre",
];
const MONTH_SHORT = ["Ene", "Feb", "Mar", "Abr", "May", "Jun", "Jul", "Ago", "Sep", "Oct", "Nov", "Dic"];
const WEEKDAY_SHORT = ["L", "M", "X", "J", "V", "S", "D"];

const iso = (y, m, d) =>
  `${y}-${String(m + 1).padStart(2, "0")}-${String(d).padStart(2, "0")}`;

const daysInMonth = (y, m) => new Date(y, m + 1, 0).getDate();

// Convert "which weekday does the 1st fall on?" to Monday-based (0..6)
const firstWeekdayMonBased = (y, m) => {
  const js = new Date(y, m, 1).getDay(); // 0=Sunday..6=Saturday
  return (js + 6) % 7; // 0=Monday..6=Sunday
};

export default function CalendarPicker({ open, onClose, onApply, initialRange }) {
  const today = useMemo(() => new Date(), []);
  const [granularity, setGranularity] = useState(() => {
    if (!initialRange?.from) return "year";
    const from = new Date(initialRange.from + "T00:00:00");
    const to = new Date((initialRange.to || initialRange.from) + "T00:00:00");
    const diff = (to - from) / 86400000;
    if (diff <= 1) return "day";
    if (diff <= 8) return "week";
    if (diff <= 31) return "month";
    return "year";
  });
  const [year, setYear] = useState(() => {
    if (initialRange?.from) return parseInt(initialRange.from.slice(0, 4), 10);
    return today.getFullYear();
  });
  const [month, setMonth] = useState(() => {
    if (initialRange?.from && (granularity === "day" || granularity === "month" || granularity === "week")) {
      return parseInt(initialRange.from.slice(5, 7), 10) - 1;
    }
    return today.getMonth();
  });

  // Selection anchors (day picker mode)
  const [rangeStart, setRangeStart] = useState(initialRange?.from || null);
  const [rangeEnd, setRangeEnd] = useState(initialRange?.to || null);

  if (!open) return null;

  const isTodayInYear = today.getFullYear() === year;

  const pickMonth = (m) => {
    // Selects the entire month
    const lastDay = daysInMonth(year, m);
    onApply({
      preset: "custom",
      from: iso(year, m, 1),
      to: iso(year, m, lastDay),
    });
    onClose();
  };

  const pickYear = (y) => {
    onApply({
      preset: `y${y}`,
      from: `${y}-01-01`,
      to: `${y}-12-31`,
    });
    onClose();
  };

  const pickDay = (y, m, d) => {
    const isoDate = iso(y, m, d);
    if (!rangeStart || (rangeStart && rangeEnd)) {
      setRangeStart(isoDate);
      setRangeEnd(null);
    } else {
      // second click: set the end (order-safe)
      if (isoDate < rangeStart) {
        setRangeEnd(rangeStart);
        setRangeStart(isoDate);
      } else {
        setRangeEnd(isoDate);
      }
    }
  };

  const applyDayRange = () => {
    if (!rangeStart) return;
    onApply({
      preset: "custom",
      from: rangeStart,
      to: rangeEnd || rangeStart,
    });
    onClose();
  };

  const pickWeek = (y, m, weekStartDay) => {
    // ISO week: 7 days starting from the given monday
    const start = new Date(y, m, weekStartDay);
    const end = new Date(start);
    end.setDate(end.getDate() + 6);
    onApply({
      preset: "custom",
      from: iso(start.getFullYear(), start.getMonth(), start.getDate()),
      to: iso(end.getFullYear(), end.getMonth(), end.getDate()),
    });
    onClose();
  };

  return (
    <div
      data-testid="calendar-picker"
      className="fixed inset-0 z-[70] flex items-center justify-center bg-black/70 backdrop-blur-sm p-4"
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-3xl max-h-[90vh] overflow-hidden rounded-2xl border border-white/10 bg-[#0F1017] shadow-2xl flex flex-col"
      >
        {/* Header */}
        <div className="px-5 py-4 border-b border-white/8 flex items-center justify-between gap-3">
          <div className="min-w-0">
            <div className="text-[10px] font-mono uppercase tracking-[0.18em] text-[#FACC15] flex items-center gap-1.5">
              <Calendar size={12} weight="bold" /> RANGO DE ANÁLISIS
            </div>
            <div className="text-lg font-heading font-bold text-white mt-0.5">
              {granularity === "year"
                ? "Selecciona un año o mes"
                : granularity === "month"
                ? "Selecciona un mes"
                : granularity === "week"
                ? "Selecciona una semana"
                : "Selecciona días"}
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            {/* Granularity toggle */}
            <div className="inline-flex items-center p-0.5 rounded-lg bg-black/40 border border-white/10">
              {[
                { key: "day", label: "Día" },
                { key: "week", label: "Semana" },
                { key: "month", label: "Mes" },
                { key: "year", label: "Año" },
              ].map((g) => {
                const active = granularity === g.key;
                return (
                  <button
                    key={g.key}
                    data-testid={`cal-gran-${g.key}`}
                    onClick={() => setGranularity(g.key)}
                    className={`px-3 py-1.5 text-[10px] font-mono uppercase tracking-widest font-bold rounded transition ${
                      active ? "on-solid bg-[#2563EB] text-white" : "text-white/50 hover:text-white/85"
                    }`}
                  >
                    {g.label}
                  </button>
                );
              })}
            </div>
            <button
              onClick={onClose}
              data-testid="calendar-picker-close"
              className="w-8 h-8 rounded-lg hover:bg-white/10 text-white/50 hover:text-white flex items-center justify-center"
            >
              <X size={14} weight="bold" />
            </button>
          </div>
        </div>

        {/* Year / month navigator (only for non-year views) */}
        {granularity !== "year" && (
          <div className="px-5 py-2 border-b border-white/6 flex items-center justify-center gap-3">
            <button
              onClick={() => {
                if (granularity === "month") {
                  setYear((y) => y - 1);
                } else {
                  const nm = month - 1;
                  if (nm < 0) { setMonth(11); setYear((y) => y - 1); }
                  else setMonth(nm);
                }
              }}
              className="w-8 h-8 rounded-lg hover:bg-white/10 text-white/60 flex items-center justify-center"
            >
              <CaretLeft size={14} weight="bold" />
            </button>
            <div className="min-w-[180px] text-center text-sm font-heading font-bold text-white">
              {granularity === "month" ? year : `${MONTH_LABELS[month]} ${year}`}
            </div>
            <button
              onClick={() => {
                if (granularity === "month") {
                  setYear((y) => y + 1);
                } else {
                  const nm = month + 1;
                  if (nm > 11) { setMonth(0); setYear((y) => y + 1); }
                  else setMonth(nm);
                }
              }}
              className="w-8 h-8 rounded-lg hover:bg-white/10 text-white/60 flex items-center justify-center"
            >
              <CaretRight size={14} weight="bold" />
            </button>
          </div>
        )}

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-5">
          {granularity === "year" && (
            <YearView
              currentYear={today.getFullYear()}
              currentMonth={today.getMonth()}
              onPickYear={pickYear}
              onPickMonth={(y, m) => { setYear(y); pickMonth(m); }}
            />
          )}
          {granularity === "month" && (
            <MonthGridForYear
              year={year}
              currentYear={today.getFullYear()}
              currentMonth={today.getMonth()}
              onPickMonth={pickMonth}
            />
          )}
          {granularity === "week" && (
            <MonthCalendarView
              year={year}
              month={month}
              mode="week"
              onPickWeek={(weekStart) => pickWeek(year, month, weekStart)}
              todayInThisMonth={today.getFullYear() === year && today.getMonth() === month ? today.getDate() : null}
            />
          )}
          {granularity === "day" && (
            <MonthCalendarView
              year={year}
              month={month}
              mode="day"
              rangeStart={rangeStart}
              rangeEnd={rangeEnd}
              onPickDay={(d) => pickDay(year, month, d)}
              todayInThisMonth={today.getFullYear() === year && today.getMonth() === month ? today.getDate() : null}
            />
          )}
        </div>

        {/* Footer with actions */}
        <div className="px-5 py-3 border-t border-white/8 flex items-center justify-between gap-3">
          <button
            data-testid="calendar-picker-clear"
            onClick={() => {
              onApply({ preset: "all", from: null, to: null });
              onClose();
            }}
            className="text-[11px] font-mono uppercase tracking-widest text-white/50 hover:text-white/80"
          >
            Ver todo
          </button>
          <div className="flex items-center gap-2">
            {granularity === "day" && rangeStart && (
              <>
                <span className="text-[11px] font-mono text-white/60">
                  {rangeStart}{rangeEnd ? ` → ${rangeEnd}` : " (día único)"}
                </span>
                <button
                  data-testid="calendar-picker-apply"
                  onClick={applyDayRange}
                  className="on-solid px-4 py-1.5 rounded-lg bg-[#FACC15] hover:bg-yellow-400 text-black font-heading font-bold text-xs"
                >
                  Aplicar
                </button>
              </>
            )}
            {granularity !== "day" && (
              <span className="text-[11px] font-mono text-white/40">
                {isTodayInYear ? `Hoy: ${today.toISOString().slice(0, 10)}` : ""}
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// ---------------- Year view — big grid of 12 months (like macOS Calendar) ----------------
function YearView({ currentYear, currentMonth, onPickYear, onPickMonth }) {
  // Show 3 years: last, current, next
  const years = [currentYear - 1, currentYear, currentYear + 1];
  return (
    <div className="space-y-6">
      {years.map((y) => (
        <div key={y}>
          <div className="flex items-center justify-between mb-3">
            <button
              data-testid={`cal-year-${y}`}
              onClick={() => onPickYear(y)}
              className="text-2xl font-heading font-black text-white hover:text-[#FACC15] transition"
            >
              {y}
            </button>
            <div className="text-[10px] font-mono uppercase tracking-widest text-white/40">
              Click un año o un mes
            </div>
          </div>
          <MonthGridForYear
            year={y}
            currentYear={currentYear}
            currentMonth={currentMonth}
            onPickMonth={(m) => onPickMonth(y, m)}
          />
        </div>
      ))}
    </div>
  );
}

function MonthGridForYear({ year, currentYear, currentMonth, onPickMonth }) {
  return (
    <div className="grid grid-cols-3 md:grid-cols-4 gap-3">
      {MONTH_LABELS.map((label, m) => {
        const isCurrent = year === currentYear && m === currentMonth;
        return (
          <button
            key={m}
            data-testid={`cal-month-${year}-${m + 1}`}
            onClick={() => onPickMonth(m)}
            className={`aspect-[1.7/1] rounded-xl border p-3 flex flex-col justify-between text-left hover:scale-[1.02] transition group ${
              isCurrent
                ? "bg-[#FACC15]/12 border-[#FACC15]/40 hover:border-[#FACC15]"
                : "bg-black/30 border-white/8 hover:border-white/25"
            }`}
          >
            <div className="flex items-baseline justify-between">
              <div className={`font-heading font-bold text-sm ${isCurrent ? "text-[#FACC15]" : "text-white group-hover:text-[#FACC15]"}`}>
                {MONTH_SHORT[m]}
              </div>
              <div className="text-[9px] font-mono uppercase tracking-widest text-white/30">
                {year}
              </div>
            </div>
            <MiniMonthGrid year={year} month={m} highlightToday={isCurrent} />
          </button>
        );
      })}
    </div>
  );
}

function MiniMonthGrid({ year, month, highlightToday }) {
  const dim = daysInMonth(year, month);
  const first = firstWeekdayMonBased(year, month);
  const cells = [];
  const today = new Date();
  const todayD = highlightToday ? today.getDate() : null;
  for (let i = 0; i < first; i++) cells.push(null);
  for (let d = 1; d <= dim; d++) cells.push(d);
  return (
    <div className="grid grid-cols-7 gap-[2px]">
      {cells.map((d, i) => (
        <div
          key={i}
          className={`text-[8px] font-mono text-center h-3 flex items-center justify-center rounded-sm ${
            d == null
              ? "opacity-0"
              : d === todayD
              ? "bg-[#FACC15] text-black font-bold"
              : "text-white/50"
          }`}
        >
          {d ?? "·"}
        </div>
      ))}
    </div>
  );
}

// ---------------- Month calendar (week / day) ----------------
function MonthCalendarView({
  year, month, mode, rangeStart, rangeEnd, onPickWeek, onPickDay, todayInThisMonth,
}) {
  const dim = daysInMonth(year, month);
  const first = firstWeekdayMonBased(year, month);

  // Build weeks
  const weeks = [];
  let day = 1;
  let leadPadding = first;
  while (day <= dim) {
    const week = [];
    for (let i = 0; i < 7; i++) {
      if (leadPadding > 0) {
        week.push(null);
        leadPadding--;
      } else if (day > dim) {
        week.push(null);
      } else {
        week.push(day++);
      }
    }
    weeks.push(week);
  }

  const isInRange = (d) => {
    if (mode !== "day" || !rangeStart) return false;
    const iso_ = iso(year, month, d);
    const s = rangeStart;
    const e = rangeEnd || rangeStart;
    return iso_ >= s && iso_ <= e;
  };
  const isRangeEdge = (d) => {
    if (mode !== "day" || !rangeStart) return false;
    const iso_ = iso(year, month, d);
    return iso_ === rangeStart || iso_ === rangeEnd;
  };

  return (
    <div>
      <div className="grid grid-cols-8 gap-1 mb-1 text-[10px] font-mono uppercase tracking-widest text-white/40">
        {mode === "week" && <div className="text-right pr-2">W</div>}
        {mode === "day" && <div />}
        {WEEKDAY_SHORT.map((w) => (
          <div key={w} className="text-center py-1">{w}</div>
        ))}
      </div>
      <div className="space-y-1">
        {weeks.map((week, wi) => {
          // Find the first real day in the week to use as the week's start
          const firstReal = week.find((d) => d != null);
          return (
            <div key={wi} className="grid grid-cols-8 gap-1 items-center">
              {mode === "week" ? (
                <button
                  data-testid={`cal-week-${year}-${month + 1}-${wi}`}
                  onClick={() => firstReal && onPickWeek?.(firstReal)}
                  disabled={!firstReal}
                  className="text-right pr-2 text-[10px] font-mono text-white/40 hover:text-[#FACC15]"
                >
                  #{wi + 1}
                </button>
              ) : (
                <div />
              )}
              {week.map((d, i) => {
                if (d == null) {
                  return <div key={i} className="h-10" />;
                }
                const isToday = d === todayInThisMonth;
                const inRange = isInRange(d);
                const isEdge = isRangeEdge(d);
                const testId =
                  mode === "day"
                    ? `cal-day-${year}-${month + 1}-${d}`
                    : `cal-week-day-${d}`;
                return (
                  <button
                    key={i}
                    data-testid={testId}
                    onClick={() => {
                      if (mode === "day") onPickDay?.(d);
                      else onPickWeek?.(week.find((x) => x != null));
                    }}
                    className={`h-10 rounded-lg text-sm font-mono font-bold flex items-center justify-center transition ${
                      isEdge
                        ? "on-solid bg-[#FACC15] text-black"
                        : inRange
                        ? "bg-[#FACC15]/15 text-[#FACC15]"
                        : isToday
                        ? "border border-[#FACC15]/50 text-white"
                        : "text-white/70 hover:bg-white/10"
                    }`}
                  >
                    {d}
                  </button>
                );
              })}
            </div>
          );
        })}
      </div>
      {mode === "day" && (
        <div className="mt-3 text-[10px] font-mono text-white/40 text-center">
          Click un día para inicio; click otro para fin del rango.
        </div>
      )}
      {mode === "week" && (
        <div className="mt-3 text-[10px] font-mono text-white/40 text-center">
          Click una semana para filtrar 7 días.
        </div>
      )}
    </div>
  );
}
